import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, FileText, MessageCircle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const STATUSES = ["nueva", "contactada", "cotizada", "cerrada"];
const BADGE = {
  nueva: "bg-[#F2B90D]/15 text-[#F2B90D] border-[#F2B90D]/40",
  contactada: "bg-blue-500/15 text-blue-400 border-blue-500/40",
  cotizada: "bg-green-500/15 text-green-400 border-green-500/40",
  cerrada: "bg-neutral-700/30 text-neutral-400 border-neutral-700",
};

export default function Inquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [filter, setFilter] = useState("todas");
  const navigate = useNavigate();

  const load = () => api.get("/inquiries").then((r) => setInquiries(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/inquiries/${id}`, { status });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toClient = async (id) => {
    try {
      const { data } = await api.post(`/inquiries/${id}/to-client`);
      toast.success(`Cliente "${data.name}" agregado`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const toQuote = async (id) => {
    try {
      const { data } = await api.post(`/inquiries/${id}/to-quote`);
      toast.success(`Cotización ${data.number} creada`);
      navigate("/panel/cotizaciones", { state: { openQuote: data.id } });
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminar esta consulta definitivamente?")) return;
    try {
      await api.delete(`/inquiries/${id}`);
      load();
      toast.success("Consulta eliminada");
    } catch (e) { toast.error(apiError(e)); }
  };

  const filtered = filter === "todas" ? inquiries : inquiries.filter((i) => i.status === filter);

  return (
    <div data-testid="inquiries-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading font-bold uppercase text-2xl tracking-tight">Consultas</h1>
        <div className="flex gap-2">
          {["todas", ...STATUSES].map((s) => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-3 py-1.5 text-xs uppercase font-bold rounded-sm border transition-colors ${filter === s ? "bg-[#F2B90D] border-[#F2B90D] text-black" : "border-neutral-700 text-neutral-400 hover:bg-neutral-800"}`}
              data-testid={`filter-${s}`}>{s}</button>
          ))}
        </div>
      </div>
      {filtered.length === 0 && <p className="text-neutral-500 text-sm">No hay consultas {filter !== "todas" ? `con estado "${filter}"` : ""}. Las consultas del formulario de la landing aparecen acá.</p>}
      <div className="space-y-4">
        {filtered.map((i) => (
          <div key={i.id} className="bg-[#171717] border border-neutral-800 rounded-md p-5" data-testid={`inquiry-card-${i.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-heading font-bold">{i.name}</h3>
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-sm border ${BADGE[i.status]}`}>{i.status}</span>
                  <span className="text-xs text-neutral-500">{new Date(i.created_at).toLocaleString("es-AR")}</span>
                </div>
                <p className="text-xs text-neutral-400 mt-1">{i.service_type} · {i.phone} {i.email ? `· ${i.email}` : ""}</p>
                <p className="text-sm text-neutral-300 mt-3 leading-relaxed">{i.message}</p>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Button size="sm" onClick={() => toQuote(i.id)} className="bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm text-xs font-heading uppercase" data-testid={`inquiry-to-quote-${i.id}`}>
                  <FileText className="w-3.5 h-3.5 mr-1.5" /> Cotizar
                </Button>
                <Button size="sm" variant="outline" onClick={() => toClient(i.id)} className="border-neutral-700 rounded-sm text-xs font-heading uppercase hover:bg-neutral-800 hover:text-white" data-testid={`inquiry-to-client-${i.id}`}>
                  <UserPlus className="w-3.5 h-3.5 mr-1.5" /> A clientes
                </Button>
                <Button size="sm" variant="outline" onClick={() => navigate("/panel/whatsapp", { state: { phone: i.phone, nombre: i.name } })} className="border-[#25D366]/50 text-[#25D366] rounded-sm text-xs font-heading uppercase hover:bg-[#25D366]/10 hover:text-[#25D366]" data-testid={`inquiry-whatsapp-${i.id}`}>
                  <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> WhatsApp
                </Button>
                <Button size="sm" variant="outline" onClick={() => remove(i.id)} className="border-neutral-700 text-red-400 rounded-sm text-xs font-heading uppercase hover:bg-red-500/10 hover:text-red-400" data-testid={`inquiry-delete-${i.id}`}>
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Eliminar
                </Button>
              </div>
            </div>
            <div className="flex gap-2 mt-4 pt-3 border-t border-neutral-800">
              <span className="text-xs text-neutral-500 mr-1 self-center">Estado:</span>
              {STATUSES.map((s) => (
                <button key={s} onClick={() => setStatus(i.id, s)}
                  className={`px-2 py-1 text-[10px] uppercase font-bold rounded-sm border transition-colors ${i.status === s ? BADGE[s] : "border-neutral-800 text-neutral-500 hover:bg-neutral-800"}`}
                  data-testid={`inquiry-status-${s}-${i.id}`}>{s}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
